import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowCounterClockwise, Trash } from "@phosphor-icons/react";
import api from "@/lib/api";
import { AdminModal, errText } from "@/components/pageSettings/AdminModal";
import { fmtDate } from "@/lib/constants";

const BTN = "border px-2 py-1 text-[10px] uppercase tracking-widest inline-flex items-center gap-1 disabled:opacity-40";

function Row({ p, busy, onRestore, onRemove }) {
  return (
    <li className="flex flex-wrap items-center gap-3 px-3 py-2 text-xs" data-testid={`deleted-page-${p.slug}`}>
      <div className="flex-1 min-w-[220px]">
        <div className="font-bold">{p.firm_name || p.slug}{p.is_sample_page && <span className="ml-1 text-[9px] border border-amber-600 bg-amber-100 px-1 uppercase">Sample</span>}</div>
        <div className="font-mono text-[10px] text-zinc-600">
          /lp/{p.slug}{p.buyer ? ` · ${p.buyer}` : ""} · deleted {fmtDate(p.deleted_at)}{p.deleted_by_email ? ` by ${p.deleted_by_email}` : ""}
        </div>
        <div className="text-[10px] text-zinc-600">
          {p.leads} lead{p.leads === 1 ? "" : "s"} came from it{p.extra_pages ? ` · ${p.extra_pages} extra page${p.extra_pages === 1 ? "" : "s"} deleted with it` : ""}
          {p.slug_taken && <span className="text-amber-800"> · its link name is now used by another page, so it comes back under a new one</span>}
        </div>
      </div>
      <button type="button" disabled={!!busy} onClick={() => onRestore(p)} data-testid={`deleted-page-restore-${p.slug}`}
        className={`${BTN} border-black hover:bg-black hover:text-white`}>
        <ArrowCounterClockwise size={10} weight="bold" /> {busy === `r${p.id}` ? "…" : "Restore"}
      </button>
      <button type="button" disabled={!!busy} onClick={() => onRemove(p)} data-testid={`deleted-page-remove-${p.slug}`}
        className={`${BTN} border-red-700 text-red-700 hover:bg-red-700 hover:text-white`}>
        <Trash size={10} weight="bold" /> {busy === `x${p.id}` ? "…" : "Delete for good"}
      </button>
    </li>
  );
}

// Deleted landing pages: bring one back (paused) or erase it for good.
export function DeletedPagesModal({ onClose, onChanged }) {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(null);
  const load = useCallback(() => api.get("/admin/landing-page-trash")
    .then((r) => setRows(r.data.pages))
    .catch((e) => { toast.error(errText(e, "Couldn't load deleted pages")); setRows([]); }), []);
  useEffect(() => { load(); }, [load]);

  const act = async (key, fn) => {
    setBusy(key);
    try {
      await fn();
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(errText(e, "That didn't work"));
    } finally {
      setBusy(null);
    }
  };
  const restore = (p) => act(`r${p.id}`, async () => {
    const { data } = await api.post(`/admin/landing-page-trash/${p.id}/restore`);
    const extra = data.extra_pages ? ` with ${data.extra_pages} extra page${data.extra_pages === 1 ? "" : "s"}` : "";
    toast.success(`Restored /lp/${data.slug}${extra} as ${data.status}. Publish it when you're ready.`);
  });
  const remove = (p) => {
    const typed = window.prompt(
      `Delete /lp/${p.slug} for good? This can't be undone.\n`
      + `Its ${p.leads} past lead${p.leads === 1 ? "" : "s"} stay in your Leads list.`
      + `${p.extra_pages ? `\nThe ${p.extra_pages} extra page${p.extra_pages === 1 ? " deleted with it goes" : "s deleted with it go"} too.` : ""}`
      + `\n\nType ${p.slug} to confirm:`,
    );
    if (typed === null) return;
    act(`x${p.id}`, async () => {
      const { data } = await api.delete(`/admin/landing-page-trash/${p.id}`, { params: { confirm_slug: typed } });
      toast.success(`Deleted for good (${data.removed} page${data.removed === 1 ? "" : "s"})`);
    });
  };

  return (
    <AdminModal eyebrow="Landing pages" title="Deleted pages" onClose={onClose} testId="deleted-pages-modal" wide>
      <p className="mb-3 text-[11px] text-zinc-600">
        Deleted pages don&apos;t show to visitors or take leads. Restore brings one back paused, so you choose when it goes live.
      </p>
      {rows === null && <div className="label-mono py-4 text-center">Loading…</div>}
      {rows && !rows.length && <div className="label-mono py-4 text-center" data-testid="deleted-pages-empty">No deleted pages</div>}
      {rows && rows.length > 0 && (
        <ul className="border border-black divide-y divide-zinc-200" data-testid="deleted-pages-list">
          {rows.map((p) => <Row key={p.id} p={p} busy={busy} onRestore={restore} onRemove={remove} />)}
        </ul>
      )}
    </AdminModal>
  );
}

// Row action on the Custom buyer landing pages table.
export function DeletePageButton({ page, submitted30d, onDeleted }) {
  const [busy, setBusy] = useState(false);
  const del = async () => {
    const sample = page.is_sample_page || (page.slug || "").startsWith("sample-");
    const lines = [
      `Delete /lp/${page.slug}${page.firm_name ? ` (${page.firm_name})` : ""}?`, "",
      "Its link stops working right away and it stops taking leads. Leads it already sent, and its reports, are kept.",
    ];
    if (submitted30d) lines.push(`It got ${submitted30d} form submission${submitted30d === 1 ? "" : "s"} in the last 30 days.`);
    if (sample) lines.push("It's a sample page used in your buyer pitches.");
    lines.push("If it's a buyer's main page, their extra pages are deleted with it.", "", "You can bring it back from Deleted pages.");
    if (!window.confirm(lines.join("\n"))) return;
    setBusy(true);
    try {
      const { data } = await api.delete(`/admin/custom-landing-pages/${page.id}`);
      const extra = data.extra_pages ? ` and ${data.extra_pages} extra page${data.extra_pages === 1 ? "" : "s"}` : "";
      toast.success(`Deleted /lp/${page.slug}${extra}. Restore it from Deleted pages.`);
      onDeleted?.();
    } catch (e) {
      toast.error(errText(e, "Delete failed"));
    } finally {
      setBusy(false);
    }
  };
  return (
    <button type="button" onClick={del} disabled={busy} data-testid={`admin-custom-lp-delete-${page.slug}`} title="Delete this page"
      className="ml-2 border border-red-700 text-red-700 px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-red-700 hover:text-white disabled:opacity-40 inline-flex items-center gap-1">
      <Trash size={10} weight="bold" /> {busy ? "…" : "Delete"}
    </button>
  );
}
